import React, { useEffect, useState } from 'react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import { ReflectionSong } from './MusicReflectionsPage';
import './MusicReflectionsPage.css';

interface Settings { enabled: boolean; require_approval: boolean; send_day: number; send_time: string; email_subject: string; email_body: string; revision: number; trial_email?: string | null; trial_until?: string | null }
interface Subscriber { id: number; name: string; email: string; status: string }
interface Week { id: number; service_date: string; service_title: string; songs: ReflectionSong[]; response_count: number }
interface Answer { name: string; email: string; selected_ids: number[]; comment: string; submitted_at: string }
interface Delivery { id: number; week_id: number; name: string; email: string; status: string; error: string | null }
interface Data { settings: Settings; subscribers: Subscriber[]; weeks: Week[]; deliveries: Delivery[] }
const problem = (e: any) => typeof e?.response?.data?.error === 'string' ? e.response.data.error : 'Could not load or save music reflections.';

export default function MusicReflectionsAdminPage() {
  const [data, setData] = useState<Data | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<Week | null>(null);
  const [answers, setAnswers] = useState<Answer[] | null>(null);
  const reload = async () => {
    setError(''); setBusy(true);
    try {
      const value = (await apiClient.get<Data>('/music-reflections/admin')).data;
      setData(value); setSettings({ ...value.settings, send_time: value.settings.send_time.slice(0, 5) });
      setSelectedWeek(null); setAnswers(null);
    } catch (e) { setError(problem(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);
  const saveSettings = async (e: React.FormEvent) => {
    e.preventDefault(); if (!settings) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const { enabled, require_approval, send_day, send_time, email_subject, email_body, revision } = settings;
      const saved = (await apiClient.put<Settings>('/music-reflections/admin/settings', { enabled, require_approval, send_day, send_time, email_subject, email_body, revision })).data;
      setSettings({ ...saved, send_time: saved.send_time.slice(0, 5) });
      setMessage(saved.enabled ? 'Settings saved. Weekly emails are enabled.' : 'Settings saved. Weekly emails are paused.');
    } catch (e) { setError(problem(e)); }
    finally { setBusy(false); }
  };
  const updateSubscriber = async (subscriber: Subscriber, status: string) => {
    setBusy(true); setError('');
    try {
      await apiClient.put(`/music-reflections/admin/subscribers/${subscriber.id}`, { status, previous_status: subscriber.status });
      setData(current => current && ({ ...current, subscribers: current.subscribers.map(s => s.id === subscriber.id ? { ...s, status } : s) }));
    } catch (e) { setError(problem(e)); }
    finally { setBusy(false); }
  };
  const showResults = async (week: Week) => {
    setBusy(true); setError(''); setSelectedWeek(week); setAnswers(null);
    try { setAnswers((await apiClient.get<Answer[]>(`/music-reflections/admin/weeks/${week.id}`)).data); }
    catch (e) { setError(problem(e)); }
    finally { setBusy(false); }
  };
  return <><Navigation /><main className="music-reflections admin-reflections">
    <h1>Music reflections</h1><p>Only your account can manage subscribers and see results.</p>
    <button className="btn btn-outline-secondary" disabled={busy} onClick={() => void reload()}>Reload</button>
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    {message && <div role="status" className="alert alert-success">{message}</div>}
    {data && settings && <>
      {settings.trial_email && <div className="alert alert-info">Private trial: emails go only to {settings.trial_email}, through {settings.trial_until}. Signup is closed. Weekly delivery pauses automatically after that date.</div>}
      <section><h2>Signup</h2>
        <p>{settings.trial_email ? 'This signup page is closed during your private trial:' : 'Share this signup link with the choir and anyone else you would like to invite:'}</p>
        <a href="/music-reflections" target="_blank" rel="noreferrer">https://sl2music.com/music-reflections</a>
        <p>Subscribers confirm their email address before they can receive a weekly link.</p>
      </section>
      <section><h2>Weekly email</h2><form onSubmit={saveSettings}><fieldset disabled={busy}>
        <label className="reflection-choice"><input type="checkbox" checked={settings.enabled} onChange={e => setSettings({ ...settings, enabled: e.target.checked })} /> Enable weekly emails</label>
        <label className="reflection-choice"><input type="checkbox" checked={settings.require_approval} onChange={e => setSettings({ ...settings, require_approval: e.target.checked })} /> Review new subscribers before their weekly emails begin</label>
        <div className="reflection-schedule">
          <label>Send on<select className="form-select" value={settings.send_day} onChange={e => setSettings({ ...settings, send_day: Number(e.target.value) })}>
            <option value={0}>Sunday</option><option value={1}>Monday</option></select></label>
          <label>Time (Pacific)<input className="form-control" type="time" required value={settings.send_time} onChange={e => setSettings({ ...settings, send_time: e.target.value })} /></label>
        </div>
        <label htmlFor="reflection-subject">Email subject</label>
        <input id="reflection-subject" className="form-control" maxLength={160} value={settings.email_subject} onChange={e => setSettings({ ...settings, email_subject: e.target.value })} />
        <label htmlFor="reflection-body">Email message</label>
        <textarea id="reflection-body" className="form-control" rows={5} maxLength={5000} value={settings.email_body} onChange={e => setSettings({ ...settings, email_body: e.target.value })} />
        <p>Use {'{date}'} for the Sunday’s date. Each email includes personal links to respond and unsubscribe.</p>
        <details><summary>Preview email</summary><strong>{settings.email_subject.replaceAll('{date}', '2026-10-18')}</strong>
          <p style={{ whiteSpace: 'pre-wrap' }}>{settings.email_body.replaceAll('{date}', '2026-10-18')}</p>
          <p>Sunday Music Reflections<br />[Personal response link]</p><p>Unsubscribe<br />[Personal unsubscribe link]</p></details>
        <button className="btn btn-primary" type="submit">Save email settings</button>
      </fieldset></form></section>
      <section><h2>Subscribers ({data.subscribers.filter(s => s.status === 'active').length} active)</h2>
        {!data.subscribers.length && <p>No signups yet.</p>}
        {data.subscribers.map(s => <div key={s.id} className="reflection-subscriber">
          <div><strong>{s.name}</strong><div>{s.email}</div><small>{s.status === 'pending' ? 'Awaiting your approval' : s.status}</small></div>
          <div>{s.status === 'pending' && <button disabled={busy} className="btn btn-outline-success" onClick={() => void updateSubscriber(s, 'active')}>Approve</button>}
            {['pending', 'active'].includes(s.status) && <button disabled={busy} className="btn btn-outline-secondary" onClick={() => void updateSubscriber(s, 'declined')}>{s.status === 'pending' ? 'Decline' : 'Stop emails'}</button>}</div>
        </div>)}
      </section>
      <section><h2>Results by Sunday</h2>
        {!data.weeks.length && <p>Results will appear here after the first weekly email.</p>}
        <div className="reflection-weeks">{data.weeks.map(w => <button className="btn btn-outline-primary" disabled={busy} key={w.id} onClick={() => void showResults(w)}>
          {w.service_date}: {w.response_count} {Number(w.response_count) === 1 ? 'response' : 'responses'}</button>)}</div>
        {selectedWeek && answers && <div><h3>{selectedWeek.service_date} · {selectedWeek.service_title}</h3>
          <p>{answers.length} {answers.length === 1 ? 'person responded' : 'people responded'}. Each count shows how many chose that song.</p>
          <ul>{selectedWeek.songs.map(song => <li key={song.id}><strong>{song.title}</strong> ({song.placement}): {answers.filter(a => a.selected_ids.includes(song.id)).length}</li>)}</ul>
          <h4>Individual responses</h4>
          {answers.map((answer, index) => <article className="reflection-answer" key={index}>
            <strong>{answer.name}</strong> <span className="text-muted">{answer.email}</span>
            <p>{selectedWeek.songs.filter(s => answer.selected_ids.includes(s.id)).map(s => s.title).join('; ') || 'No songs selected'}</p>
            <p style={{ whiteSpace: 'pre-wrap' }}>{answer.comment || 'No comment'}</p>
          </article>)}
        </div>}
      </section>
      <section><h2>Email delivery</h2>
        <p>Failed or interrupted sends are kept here for review and are not automatically sent again.</p>
        {!data.deliveries.length && <p>No weekly emails yet.</p>}
        {data.deliveries.map(d => <p key={d.id}><strong>{d.name}</strong> ({d.email}): {d.status === 'sending' ? 'Sending, or interrupted before confirmation' : d.status}{d.error ? ` · ${d.error}` : ''}</p>)}
      </section>
    </>}
  </main></>;
}
